import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AdminDialogState } from 'admin/Admin';
import { Box_Insert_Input } from 'generated/graphql';
import React, { useCallback, useState } from 'react';
import UpsertBoxForm, { FormData } from './UpsertBoxForm';
import useInsertAdminBox from './useInsertAdminBox';
import useUpdateAdminBox from './useUpdateAdminBox';
import Box from '@material-ui/core/Box';
import SaveIcon from '@material-ui/icons/Save';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles, Theme } from '@material-ui/core/styles';

interface Props {
  dialog: AdminDialogState;
  closeDialog: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  deleteBtn: {
    backgroundColor: theme.palette.error.dark,
    justifySelf: 'start',
  },
}));

const UpsertBoxDialog = ({ dialog, closeDialog }: Props) => {
  const classes = useStyles();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [insertBoxMutation, insertStatus] = useInsertAdminBox(closeDialog);

  const [updateBoxMutation, updateStatus] = useUpdateAdminBox(closeDialog);

  const onDeleteBtnClick = () => setIsDeleteConfirmOpen(true);
  const onCancelDelete = () => setIsDeleteConfirmOpen(false);

  const onSubmit = useCallback(
    (data: FormData) => {
      const box: Box_Insert_Input = {
        name: data.name,
        imei: data.imei,
        plate_number: data.plateNumber ? data.plateNumber : null,
        postcode: data.postcode ? data.postcode.replace('-', '') : null,
        prefecture: data.prefecture.value ? data.prefecture.value : null,
        address: data.address ? data.address : null,
        coords: {
          type: 'Point',
          coordinates: [data.lng, data.lat],
        },
      };

      if (data.id) {
        updateBoxMutation({ boxId: data.id, box });
      } else {
        insertBoxMutation({ box });
      }
    },
    [insertBoxMutation, updateBoxMutation]
  );

  const isCreate = dialog.type === 'create';
  const title = isCreate ? 'ボックスを追加する' : 'ボックスを更新する';

  const confirm = isCreate ? '追加する' : '更新する';

  const box = dialog.type !== 'create' ? dialog.type : undefined;

  const isLoading = insertStatus.isLoading || updateStatus.isLoading;

  return (
    <>
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>

      <DialogContent>
        <UpsertBoxForm box={box} onSubmit={onSubmit} />
      </DialogContent>
      <DialogActions>
        <Box
          display="grid"
          gridTemplateColumns={
            dialog.type === 'create' ? 'auto auto' : '1fr auto auto'
          }
          width="100%"
          justifyContent="end"
          gridGap="30px"
        >
          {box ? (
            <Button
              startIcon={<DeleteIcon />}
              onClick={onDeleteBtnClick}
              className={classes.deleteBtn}
              variant="contained"
              color="secondary"
              disabled={isLoading}
            >
              削除
            </Button>
          ) : null}
          <Button onClick={closeDialog} color="primary">
            キャンセル
          </Button>
          <Button
            type="submit"
            form="addBoxForm"
            color="primary"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isLoading}
          >
            {confirm}
          </Button>
        </Box>
      </DialogActions>
      {box ? (
        <DeleteConfirmDialog
          boxIds={[box.id]}
          onDeleteSuccess={closeDialog}
          isOpen={isDeleteConfirmOpen}
          onCancelDelete={onCancelDelete}
        />
      ) : null}
    </>
  );
};

export default UpsertBoxDialog;
